/**
 * Get the base URL for the application
 * Handles different environments (Vercel, ngrok, localhost)
 */
import { env } from "@repo/env";

export const getBaseUrl = (): string => {
  // For client-side, return current origin
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  // For server-side rendering, ensure we always return a valid URL
  if (env.NEXT_PUBLIC_SITE_URL) {
    // Remove protocol if it exists to avoid double protocol
    const cleanUrl = env.NEXT_PUBLIC_SITE_URL.replace(/^https?:\/\//, "");
    return `https://${cleanUrl}`;
  }

  // Safe fallback for localhost
  return `http://localhost:${env.PORT ?? 3000}`;
};

/**
 * Get the app URL, resolving preview environments to *.preview.terac.com.
 * Use this instead of inlining `process.env.NEXT_PUBLIC_APP_URL || "https://terac.com"`.
 */
export const getAppUrl = (): string => {
  if (env.NEXT_PUBLIC_VERCEL_ENV === "preview") {
    const gitRef = env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF;
    if (gitRef) {
      if (gitRef === "main") {
        return "https://preview.terac.com";
      }
      const sanitized = gitRef
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 63);
      return `https://${sanitized}.preview.terac.com`;
    }
  }
  return env.NEXT_PUBLIC_APP_URL ?? "https://terac.com";
};

/**
 * Absolute URL for a relative in-app path, normally one built by `webPaths`.
 * Keeps callers that need to leave the app (emails, Slack, API responses) from
 * re-inlining the `getAppUrl()` + path composition.
 */
export const teracUrl = (path: string): string => {
  return `${getAppUrl()}${path}`;
};

/**
 * Build a referral URL from a token
 * Uses NEXT_PUBLIC_APP_URL for environment-appropriate URLs
 */
export const buildReferralUrl = (token: string): string => {
  return `${getAppUrl()}/refer/${token}`;
};

/**
 * Relative path to an organization's internal dashboard page. Single source of
 * truth for this route: the page lives at
 * `app/(internal)/internal/organizations/[orgId]/settings`, so a future rename
 * only needs to change here. Use for in-app navigation (`<Link href>` /
 * `router.push`).
 */
export const internalOrgPath = (organizationId: string): string => {
  return `/internal/organizations/${organizationId}/settings`;
};

/**
 * Absolute URL to an organization's internal dashboard page, for links that
 * leave the app (Slack alerts, emails). In-app navigation should use the
 * relative `internalOrgPath` instead.
 */
export const buildInternalOrgUrl = (organizationId: string): string => {
  return `${getAppUrl()}${internalOrgPath(organizationId)}`;
};

export interface RecruitmentInterviewUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
}

/** Build the `/interview/start/r/{token}` URL with optional UTM attribution. */
export const buildRecruitmentInterviewUrl = (
  recruitmentToken: string,
  utm: RecruitmentInterviewUtm = {},
): string => {
  const params = new URLSearchParams();
  if (utm.source) {
    params.set("utm_source", utm.source);
  }
  if (utm.medium) {
    params.set("utm_medium", utm.medium);
  }
  if (utm.campaign) {
    params.set("utm_campaign", utm.campaign);
  }
  if (utm.content) {
    params.set("utm_content", utm.content);
  }
  const query = params.toString();
  return `${getAppUrl()}/interview/start/r/${recruitmentToken}${query ? `?${query}` : ""}`;
};

export interface OpportunitiesUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
}

/** Build the public `/opportunities` URL with optional UTM attribution. */
export const buildOpportunitiesUrl = (utm: OpportunitiesUtm = {}): string => {
  const params = new URLSearchParams();
  if (utm.source) {
    params.set("utm_source", utm.source);
  }
  if (utm.medium) {
    params.set("utm_medium", utm.medium);
  }
  if (utm.campaign) {
    params.set("utm_campaign", utm.campaign);
  }
  if (utm.content) {
    params.set("utm_content", utm.content);
  }
  const query = params.toString();
  return `${getAppUrl()}/opportunities${query ? `?${query}` : ""}`;
};

/**
 * The query parameter a shared public listing carries the sharer's own
 * recruitment link in. The listing forwards it into the application, and the
 * apply flow enters through that link instead of the listing's own.
 */
export const LISTING_SHARE_LINK_PARAM = "via";

/** The public listing URL a staff member shares, carrying their own link. */
export const buildListingShareUrl = (
  listingToken: string,
  recruitmentToken: string,
): string => {
  const params = new URLSearchParams({
    [LISTING_SHARE_LINK_PARAM]: recruitmentToken,
  });
  return `${getAppUrl()}/opportunities/${listingToken}?${params.toString()}`;
};
